import { useEffect, useRef } from "react";
import {
  checkAdminSession,
  checkCustomerSession,
  forceAdminLogout,
  logoutAdminSession,
  logoutCustomerSession,
} from "../utils/api";
import useStore from "../utils/store";

const IDLE_MS = 10 * 60 * 1000;
const POLL_MS = 5 * 1000;
const IDLE_CHECK_MS = 5 * 1000;
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

const SESSION_CONFIG = {
  Admin: {
    activityKey: "prabhu_admin_last_activity",
    policyKey: "prabhu_admin_session_v2",
    check: checkAdminSession,
    logout: logoutAdminSession,
  },
  Customer: {
    activityKey: "prabhu_customer_last_activity",
    policyKey: "prabhu_customer_session_v1",
    check: checkCustomerSession,
    logout: logoutCustomerSession,
  },
};

const getPersistedUser = () => {
  try {
    const raw = localStorage.getItem("prabhu");
    if (!raw) return null;
    return JSON.parse(raw)?.state?.user ?? null;
  } catch {
    return null;
  }
};

const getTokenPayload = (token) => {
  try {
    const part = token.split(".")[1];
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
};

const AdminSessionGuard = () => {
  const user = useStore((state) => state.user);
  const setUser = useStore((state) => state.setUser);
  const loggingOut = useRef(false);

  useEffect(() => {
    const config = SESSION_CONFIG[user?.type];
    if (!config || !user?.token) {
      return;
    }

    loggingOut.current = false;
    let lastWrite = 0;

    const markActivity = () => {
      const now = Date.now();
      if (now - lastWrite < 1000) return;
      lastWrite = now;
      localStorage.setItem(config.activityKey, String(now));
    };

    const isIdle = () => {
      const last = Number(localStorage.getItem(config.activityKey) || 0);
      return Date.now() - last >= IDLE_MS;
    };

    const logout = async (reason) => {
      if (loggingOut.current) return;
      loggingOut.current = true;
      if (reason !== "SESSION_REPLACED" && user?.token) {
        await config.logout(user.token);
      }
      forceAdminLogout(reason);
    };

    if (localStorage.getItem(config.policyKey) !== "1") {
      localStorage.setItem(config.policyKey, "1");
      const existingPayload = getTokenPayload(user.token);
      if (!existingPayload?.sessionId) {
        logout("SESSION_REPLACED");
        return;
      }
    }

    const payload = getTokenPayload(user.token);
    if (!payload?.sessionId) {
      logout("SESSION_REPLACED");
      return;
    }

    markActivity();

    const poll = async () => {
      if (loggingOut.current) return;

      if (isIdle()) {
        await logout("SESSION_EXPIRED");
        return;
      }

      const persisted = getPersistedUser();
      if (persisted?.token && persisted.token !== user.token) {
        setUser(persisted);
        return;
      }

      const last = Number(localStorage.getItem(config.activityKey) || 0);
      const recentlyActive = Date.now() - last < POLL_MS + 1000;
      const [ok, data] = await config.check(user.token, recentlyActive);

      if (!ok) {
        const code = data?.code;
        if (
          code !== "SESSION_REPLACED" &&
          code !== "SESSION_EXPIRED" &&
          code !== "UNAUTHORIZED"
        ) {
          return;
        }

        const latest = getPersistedUser();
        if (latest?.token && latest.token !== user.token) {
          setUser(latest);
          return;
        }

        await logout(
          code === "SESSION_REPLACED" ? "SESSION_REPLACED" : "SESSION_EXPIRED"
        );
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, markActivity, { passive: true });
    });

    const onVisibility = () => {
      if (document.visibilityState === "visible" && isIdle()) {
        logout("SESSION_EXPIRED");
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    const idleTimer = setInterval(() => {
      if (isIdle()) {
        logout("SESSION_EXPIRED");
      }
    }, IDLE_CHECK_MS);

    const pollTimer = setInterval(poll, POLL_MS);
    poll();

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity);
      });
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(idleTimer);
      clearInterval(pollTimer);
    };
  }, [user?.type, user?.token, setUser]);

  return null;
};

export default AdminSessionGuard;
